import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { bootstrapTheme } from "@/lib/darkMode";

// One-shot: apply saved theme BEFORE first render so we don't flash light.
bootstrapTheme();

import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import Products from "./pages/Products";
import Orders from "./pages/Orders";
import Costing from "./pages/Costing";
import Suppliers from "./pages/Suppliers";
import RoutePlanning from "./pages/RoutePlanning";
import Documents from "./pages/Documents";
import MyCompany from "./pages/MyCompany";
import Settings from "./pages/Settings";
import PurchaseOrders from "./pages/PurchaseOrders";
import Tasks from "./pages/Tasks";
import HR from "./pages/HR";
import Analytics from "./pages/Analytics";
import Sellers from "./pages/Sellers";
import Expenses from "./pages/Expenses";
import Inventory from "./pages/Inventory";
import Invoices from "./pages/Invoices";
import Receivables from "./pages/Receivables";
import Returns from "./pages/Returns";
import Insights from "./pages/Insights";
import DashboardCustomize from "./pages/DashboardCustomize";
import ImportExcel from "./pages/ImportExcel";
import BankAccounts from "./pages/BankAccounts";
import SellerLayout from "./pages/seller/SellerLayout";
import SellerOrderTake from "./pages/seller/SellerOrderTake";
import SellerOrdersList from "./pages/seller/SellerOrdersList";
import SellerCustomers from "./pages/seller/SellerCustomers";
import SellerProducts from "./pages/seller/SellerProducts";
import Layout from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import { setActiveCurrency } from "./lib/money";
import { initNative } from "./lib/native";
import { SubscriptionProvider } from "./lib/subscription";
import Upgrade from "./pages/Upgrade";
import PublicView from "./pages/PublicView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use((response) => {
  try {
    const url = response.config?.url || "";
    if (typeof url === "string" && url.endsWith("/company") && response.data?.moneda) {
      setActiveCurrency(response.data.moneda);
    }
  } catch { /* ignore */ }
  return response;
});

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" />;
};

/** Redirect sellers to /seller, keep admins on the regular admin layout. */
function RoleAwareRedirect({ children, requireAdmin = false }) {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (data.role === "seller") {
          navigate("/seller/take", { replace: true });
          return;
        }
        if (requireAdmin && data.role !== "admin") {
          navigate("/", { replace: true });
          return;
        }
      } catch { /* not logged in — children's PrivateRoute handles it */ }
      setReady(true);
    })();
  }, [navigate, requireAdmin]);
  return ready ? children : null;
}

function App() {
  useEffect(() => {
    initNative();
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <SubscriptionProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/google/callback" element={<AuthCallback />} />
            <Route path="/p/:token" element={<PublicView />} />

          {/* Seller area (simplified layout) */}
          <Route
            path="/seller"
            element={
              <PrivateRoute>
                <SellerLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="take" replace />} />
            <Route path="take" element={<SellerOrderTake />} />
            <Route path="orders" element={<SellerOrdersList />} />
            <Route path="customers" element={<SellerCustomers />} />
            <Route path="products" element={<SellerProducts />} />
            <Route path="receivables" element={<Receivables />} />
          </Route>

          {/* Admin area — redirects sellers to /seller automatically */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <RoleAwareRedirect>
                  <Layout />
                </RoleAwareRedirect>
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="customers" element={<Customers />} />
            <Route path="products" element={<Products />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="orders" element={<Orders />} />
            <Route path="routes" element={<RoutePlanning />} />
            <Route path="costing" element={<Costing />} />
            <Route path="documents" element={<Documents />} />
            <Route path="company" element={<MyCompany />} />
            <Route path="settings" element={<Settings />} />
            <Route path="purchase-orders" element={<PurchaseOrders />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="hr" element={<HR />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="sellers" element={<Sellers />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="receivables" element={<Receivables />} />
            <Route path="returns" element={<Returns />} />
            <Route path="insights" element={<Insights />} />
            <Route path="dashboard/customize" element={<DashboardCustomize />} />
            <Route path="import" element={<ImportExcel />} />
            <Route path="bank-accounts" element={<BankAccounts />} />
            <Route path="upgrade" element={<Upgrade />} />
          </Route>
          </Routes>
        </SubscriptionProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
